import { useMemo } from "react";
import {
  Form,
  Modal,
  Checkbox,
  Flex,
  Typography,
} from "antd";

import NumberInput from "./components/NumberInput";

import { getPluralizedWord } from "@/utils";
import {
  MAX_NUMBER_INPUT_VALUE,
  NUMBER_INPUT_RANGE_MESSAGE,
  parseNumberInput,
} from "@/utils/fieldValueConverter";

const { Text } = Typography;

const PlannedLaborCostsModal = ({
  data,
  visible,
  onCancel,
  onSave,
  parentDoNotExceedChildren = false,
  plannedUpLimit = null,
  plannedLowLimit = null
}) => {
  const initialValues = useMemo(
    () => ({
      planned_costs: data?.planned_costs || null,
      require_actuals_on_acceptance: data?.require_actuals_on_acceptance || false,
      do_not_exceed_children: parentDoNotExceedChildren ? true : data?.do_not_exceed_children || false,
    }),
    [data, parentDoNotExceedChildren]
  );

  // Форма 
  const [form] = Form.useForm();

  const handleCancel = () => {
    // Запоминаем значения формы при отмене
    onCancel(form.getFieldsValue());
  };

  const handleSave = async (values) => {
    const hasValues =
      Boolean(values.planned_costs) ||
      Boolean(values.require_actuals_on_acceptance) ||
      Boolean(values.do_not_exceed_children);

    const hasChanges = data
      ? (
        values.planned_costs !== data.planned_costs ||
        Boolean(values.require_actuals_on_acceptance) !== Boolean(data.require_actuals_on_acceptance) ||
        Boolean(values.do_not_exceed_children) !== Boolean(data.do_not_exceed_children)
      )
      : false;

    if (!hasValues && !hasChanges) return;

    console.log(values)

    onSave(values);
  };

  const onOk = () => {
    form
      .validateFields()
      .then((values) => {
        handleSave(values);
      })
      .catch((info) => {
        console.log("Ошибка валидации:", info);
      });
  };

  const hoursRaw = Form.useWatch("planned_costs", form);
  const doNotExceedChildren = Form.useWatch("do_not_exceed_children", form);

  const hoursNumber =
    typeof hoursRaw === "number"
      ? hoursRaw
      : Number((hoursRaw ?? "").toString().replace(",", ".")) || null;

  const hoursInt = Math.trunc(hoursNumber);

  const hoursLabel = getPluralizedWord(hoursInt, ["час", "часа", "часов"]);

  // нижний лимит: только если есть do_not_exceed_children и lowLimit != 0
  const minPlanned =
    doNotExceedChildren &&
      plannedLowLimit != null &&
      plannedLowLimit !== 0
      ? plannedLowLimit
      : 0;

  // верхний лимит: только если родительский флаг true и есть upLimit
  const maxPlanned =
    parentDoNotExceedChildren && plannedUpLimit != null
      ? plannedUpLimit
      : undefined;

  const noParentLimit =
    parentDoNotExceedChildren && maxPlanned === 0;

  return (
    <Modal
      onOk={onOk}
      onCancel={handleCancel}
      title={
        <Flex align="center" gap={8}>
          {/* <Button
            icon={<ChevronLeftIcon style={{ fontSize: 16 }} />}
            onClick={() => handleCancel()}
          /> */}
          <span>Планируемые трудозатраты</span>
        </Flex>
      }
      open={visible}
      width={600}
      okText="Подтвердить"
      cancelText="Назад"
      style={{ paddingRight: 20 }}
    >
      <Form
        form={form}
        initialValues={initialValues}
        layout="vertical"
        onFinish={handleSave}
      >
        <Flex vertical gap={12}>
          <Flex vertical gap={4}>
            <Flex align="center" gap={6}>
              <Form.Item
                name="planned_costs"
                noStyle
                dependencies={["do_not_exceed_children"]}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const checkbox = getFieldValue("do_not_exceed_children");
                      const num = parseNumberInput(value);

                      if (
                        value !== null &&
                        value !== undefined &&
                        value !== "" &&
                        (!Number.isFinite(num) || num > MAX_NUMBER_INPUT_VALUE)
                      ) {
                        return Promise.reject(
                          new Error(NUMBER_INPUT_RANGE_MESSAGE),
                        );
                      }

                      if (
                        Number.isFinite(num) &&
                        (num < minPlanned ||
                          (maxPlanned !== undefined && num > maxPlanned))
                      ) {
                        return Promise.reject(
                          new Error("Число выходит за допустимый диапазон"),
                        );
                      }

                      if (!checkbox || parentDoNotExceedChildren) {
                        return Promise.resolve();
                      }

                      if (num > 0) {
                        return Promise.resolve();
                      }

                      return Promise.reject(new Error("Укажите положительное значение"));
                    },
                  }),
                ]}
              >
                <NumberInput
                  style={{ width: 80 }}
                  placeholder="0"
                  min={minPlanned}
                  max={Math.min(
                    maxPlanned ?? MAX_NUMBER_INPUT_VALUE,
                    MAX_NUMBER_INPUT_VALUE,
                  )}
                  onStep={(value) => {
                    form.setFieldsValue({ planned_costs: value });
                  }}
                />
              </Form.Item>

              <span>{hoursLabel}</span>
            </Flex>

            <Form.Item noStyle shouldUpdate>
              {() => {
                const errors = form.getFieldError("planned_costs");
                const hasErrors = errors.length > 0;

                return (
                  <>
                    {hasErrors && (
                      <Text type="danger" style={{ fontSize: 12 }}>
                        {errors[0]}
                      </Text>
                    )}

                    {noParentLimit && (
                      <Text
                        type="danger"
                        style={{ fontSize: 12, display: "block", marginTop: 2 }}
                      >
                        В родительской задаче не осталось свободного лимита по планируемым
                        трудозатратам для дочерних задач
                      </Text>
                    )}
                  </>
                );
              }}
            </Form.Item>
          </Flex>
          <div>
            <Form.Item
              name="require_actuals_on_acceptance"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Checkbox>Обязательное заполнение фактических трудозатрат</Checkbox>
            </Form.Item>
            <Form.Item
              name="do_not_exceed_children"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Checkbox disabled={!hoursNumber || parentDoNotExceedChildren} >Запрет превышения плановых затрат в дочерних задачах</Checkbox>
            </Form.Item>
          </div>
        </Flex>
      </Form>
    </Modal>
  );
};

export default PlannedLaborCostsModal;
